<template>
  <Bar />
  <div class="background">
    <div class="servers">
      <div class="view-controls">
        <input type="text" v-model="searchQuery" class="search-bar" placeholder="Search servers..." />
      </div>

      <table class="servers-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Description</th>
            <th>Owner</th>
            <th>Memory</th>
            <th>Disk</th>
            <th>CPU</th>
            <th>Location</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="server in filteredServers" :key="server['uuidShort']">
            <td>{{ server["name"] }}</td>
            <td>{{ server["description"] || "-" }}</td>
            <td>{{ server["owner_name"] }}</td>
            <td>{{ formatValue(server.memory, "MB") }}</td>
            <td>{{ formatValue(server.disk, "MB") }}</td>
            <td>{{ formatValue(server.cpu, "%") }}</td>
            <td>{{ server["node"] }}</td>
            <td>
              <div class="actions">
                <button class="view-button"
                  @click="openInNewTab(`https://panel.apfel-hosting.de/server/${server.uuidShort}`)">
                  <img src="@/assets/eye.png" alt="eye">
                </button>
                <button class="delete-button" @click="toggleConfirmWindow(server.id)">
                  <img src="@/assets/trash.png" alt="trash">
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="footer-space"></div>
    </div>
  </div>

  <div v-if="confirmWindowOpen" class="popup-overlay" @click="handleOverlayClick">
    <div class="confirm-popup" ref="popup">
      <h3>Confirm Deletion</h3>
      <p>Are you sure you want to delete this server?</p>
      <div class="popup-actions">
        <button class="confirm-button" @click="deleteS(deleteServerId)">Yes, Delete</button>
        <button class="cancel-button" @click="toggleConfirmWindow()">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import Bar from '@/components/Bar.vue';
import { deleteServer, getAllServerData } from '@/composables/api';
import router from '@/router';
import { updateStoreData } from '@/composables/store';

const store = useStore();
const sidebarWidth = computed(() => store.getters.sidebarWidth);

const servers = ref([]);

const confirmWindowOpen = ref(false);
const deleteServerId = ref(null);
const popup = ref(null);

const toggleConfirmWindow = (id = null) => {
  confirmWindowOpen.value = !confirmWindowOpen.value;
  deleteServerId.value = id;
};

const handleOverlayClick = (event) => {
  if (popup.value && !popup.value.contains(event.target)) {
    toggleConfirmWindow();
  }
};

const deleteS = async (id) => {
  const code = await deleteServer(id);
  if (code.code === 200) {
    servers.value = servers.value.filter(server => parseInt(server.id) !== parseInt(id));
    toggleConfirmWindow();
  } else if ([401, 403, 404].includes(code.code)) {
    router.push("/servers");
  }
};

const searchQuery = ref('');

const openInNewTab = (url) => {
  window.open(url, '_blank');
};

const filteredServers = computed(() =>
  servers.value.filter(server =>
    server.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    server.owner_name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
    (server.owner_id && String(server.owner_id).includes(searchQuery.value))
  )
);

const formatValue = (value, unit) => {
  return value === 0 ? "Unlimited" : `${value} ${unit}`;
};

onMounted(async () => {
  const success = await updateStoreData();
  if (!success) {
    router.push('/login');
    return;
  }
  const result = await getAllServerData();
  if (result.success === 400) {
    router.push('/login');
  } else if (result.success === 403) {
    router.push('/dashboard');
  } else {
    servers.value = result.servers;
  }
});
</script>

<style>
.servers {
  padding-top: 100px;
  text-align: center;
  min-height: 100vh;
  transition: margin-left 0.3s ease, width 0.3s ease;
  margin-left: calc(var(--sidebar-width));
  overflow-y: auto;
}

.view-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 20px;
}

.servers-table {
  width: 80%;
  margin: 0 auto;
  border-collapse: collapse;
  text-align: left;
  margin-bottom: 20px;
}

.servers-table th,
.servers-table td {
  padding: 10px 15px;
  border: 1px solid #ddd;
}

.servers-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.search-bar {
  width: 300px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}

.footer-space {
  height: 20px;
}

.view-button {
  background-color: #4CAF50;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
  height: 48px;
  margin-right: 10px;
}

.delete-button {
  background-color: #e01c1c;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.view-button:hover {
  background-color: #45a049;
  transform: scale(1.05);
}

.delete-button:hover {
  background-color: #a70e0e;
  transform: scale(1.05);
}

.actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.actions img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  border-radius: 4px;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.confirm-popup {
  background: white;
  padding: 20px 30px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.confirm-popup h3 {
  margin: 0 0 10px;
}

.popup-actions {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
}

.confirm-button {
  background-color: #e74c3c;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.cancel-button {
  background-color: #95a5a6;
  color: white;
  padding: 10px 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

.confirm-button:hover {
  background-color: #c0392b;
}

.cancel-button:hover {
  background-color: #7f8c8d;
}
</style>
